import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { ArrowBackOutlined } from '@mui/icons-material';
import { Alert, Box, Button, Divider, MenuItem, Stack, Typography } from '@mui/material';
import { DetailDrawer, DetailDrawerBody, FeedbackSnackbar, FormTextField, ListRow, ResourceState, SearchField } from '../../../design-system';
import type { BulkOperationsPort } from '../api/port';
import { createFixtureBulkReferencePort } from '../fixtures/bulkReferences';
import { listFixtureBulkOperations, getFixtureBulkOperation, saveFixtureBulkOperation } from '../fixtures/bulkOperationStore';
import { assertBulkOperationValid } from '../model/applyValidation';
import type { BulkOperationDetail, BulkOperationJob, BulkOperationReceipt, BulkValidatedRow } from '../model/bulkOperations';
import { BulkOperationResultDialog } from './BulkOperationResultDialog';
import { bulkDateLabel, bulkOperationLabel, bulkStatusLabel, contextualBulkWorkflows, isBulkJobActive } from '../model/presentation';
import type { BulkReferencePort } from '../model/references';
import { BulkOperationResults, BulkStatus } from './BulkOperationResults';
import { BulkPasteDialog } from './BulkPasteDialog';

export type BulkOperationsWorkspaceActions = {
  openCreate: (operation: string) => void;
  openHistory: () => void;
  openJob: (id: string, operationType?: string, receipt?: BulkOperationReceipt) => void;
};

export type BulkOperationsWorkspaceProps = {
  port: BulkOperationsPort;
  referencePort?: BulkReferencePort;
  fixtureMode: boolean;
  canRead: boolean;
  canCreate: boolean;
  canRun: boolean;
  allowedOperationTypes: readonly string[];
  title: string;
  onCompleted?: () => void;
  onOpenStaffingChange?: (id: string) => void;
  children: (actions: BulkOperationsWorkspaceActions) => ReactNode;
};

type Resource<T> = { data?: T; loading: boolean; error?: string };
const messageOf = (error: unknown, fallback: string) => error instanceof Error ? error.message : fallback;

export function BulkOperationsWorkspace({ port, referencePort, fixtureMode, canRead, canCreate, canRun, allowedOperationTypes, title, onCompleted, onOpenStaffingChange, children }: BulkOperationsWorkspaceProps) {
  const mounted = useRef(true);
  useEffect(() => { mounted.current = true; return () => { mounted.current = false; }; }, []);
  const scopeKey = JSON.stringify([...allowedOperationTypes].sort());
  const allowed = useMemo<readonly string[]>(() => JSON.parse(scopeKey) as string[], [scopeKey]);
  const context = useRef({ port, fixtureMode, scopeKey, epoch: 0 });
  if (context.current.port !== port || context.current.fixtureMode !== fixtureMode || context.current.scopeKey !== scopeKey) {
    context.current = { port, fixtureMode, scopeKey, epoch: context.current.epoch + 1 };
  }
  const historyContext = useRef(-1);
  const detailContext = useRef(-1);
  const references = useMemo(() => fixtureMode ? createFixtureBulkReferencePort() : referencePort, [fixtureMode, referencePort]);
  const [fixtureJobs, setFixtureJobs] = useState(listFixtureBulkOperations);
  const [operation, setOperation] = useState<string>();
  const [view, setView] = useState<'history' | 'detail'>();
  const [selectedId, setSelectedId] = useState<string>();
  const [selectedOperation, setSelectedOperation] = useState<string>();
  const receipts = useRef(new Map<string, BulkOperationReceipt>());
  const [history, setHistory] = useState<Resource<BulkOperationJob[]>>({ loading: true });
  const [detail, setDetail] = useState<Resource<BulkOperationDetail>>({ loading: true });
  const [historyVersion, setHistoryVersion] = useState(0);
  const [detailVersion, setDetailVersion] = useState(0);
  const [query, setQuery] = useState('');
  const [status, setStatus] = useState('');
  const [visibleCount, setVisibleCount] = useState(25);
  const [notice, setNotice] = useState('');
  const [submitError, setSubmitError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const applying = useRef(false);
  const submittingRef = useRef(false);
  const capabilities = useRef({ canRead, canCreate, canRun, allowed });
  capabilities.current = { canRead, canCreate, canRun, allowed };
  const onCompletedRef = useRef(onCompleted);
  onCompletedRef.current = onCompleted;
  const completedJobs = useRef({ epoch: context.current.epoch, keys: new Set<string>() });
  const notifyCompleted = useCallback((jobs: readonly BulkOperationJob[], epoch: number) => {
    const callback = onCompletedRef.current;
    if (!mounted.current || epoch !== context.current.epoch || !capabilities.current.canRead || !callback) return;
    if (completedJobs.current.epoch !== epoch) completedJobs.current = { epoch, keys: new Set() };
    for (const job of jobs) {
      if (!capabilities.current.allowed.includes(job.operationType) || !['COMPLETED', 'COMPLETED_WITH_ERRORS'].includes(job.status)) continue;
      const key = JSON.stringify([job.id, job.status, job.completedAt ?? '']);
      if (completedJobs.current.keys.has(key)) continue;
      completedJobs.current.keys.add(key);
      callback();
    }
  }, []);
  const dialogWorkflows = useMemo(() => operation ? contextualBulkWorkflows(operation, allowed) : [], [operation, allowed]);

  useEffect(() => {
    setOperation(undefined);
    setView(undefined);
    setSelectedId(undefined);
    receipts.current.clear();
    setHistory({ loading: true });
    setDetail({ loading: true });
    setSubmitError('');
  }, [port, fixtureMode, scopeKey]);
  useEffect(() => {
    if (!canRead) { setView(undefined); setSelectedId(undefined); receipts.current.clear(); setHistory({ loading: true }); setDetail({ loading: true }); }
    if (!canCreate || !canRun || (operation && !allowed.includes(operation))) setOperation(undefined);
  }, [canRead, canCreate, canRun, allowed, operation]);

  const drawerOpen = canRead && view !== undefined;
  useEffect(() => {
    if (!drawerOpen) return;
    let active = true;
    const epoch = context.current.epoch;
    let timer: number | undefined;
    const load = async () => {
      setHistory((current) => ({ ...current, loading: true, error: undefined }));
      try {
        const jobs = fixtureMode ? listFixtureBulkOperations() : await port.listBulkOperations();
        if (!active || epoch !== context.current.epoch) return;
        const data = jobs.filter((job) => allowed.includes(job.operationType)).sort((left, right) => right.createdAt.localeCompare(left.createdAt));
        historyContext.current = epoch;
        setHistory({ data, loading: false });
        notifyCompleted(data, epoch);
        if (!fixtureMode && data.some((job) => isBulkJobActive(job.status))) timer = window.setTimeout(() => void load(), 2_000);
      } catch (error) {
        if (active && epoch === context.current.epoch) setHistory((current) => ({ ...current, loading: false, error: messageOf(error, '처리 내역을 불러오지 못했습니다.') }));
      }
    };
    void load();
    return () => { active = false; window.clearTimeout(timer); };
  }, [drawerOpen, port, fixtureMode, fixtureJobs, allowed, historyVersion, notifyCompleted]);

  useEffect(() => {
    if (!drawerOpen || view !== 'detail' || !selectedId) return;
    let active = true;
    const epoch = context.current.epoch;
    let timer: number | undefined;
    const load = async () => {
      setDetail((current) => ({ data: current.data?.id === selectedId ? current.data : undefined, loading: true }));
      try {
        const job = fixtureMode ? getFixtureBulkOperation(selectedId) : await port.getBulkOperation(selectedId);
        if (!active || epoch !== context.current.epoch) return;
        if (!job || !allowed.includes(job.operationType)) throw new Error('이 화면에서 확인할 수 없는 작업입니다.');
        detailContext.current = epoch;
        setDetail({ data: job, loading: false });
        notifyCompleted([job], epoch);
        if (!fixtureMode && isBulkJobActive(job.status)) timer = window.setTimeout(() => void load(), 2_000);
      } catch (error) {
        if (active && epoch === context.current.epoch) setDetail((current) => ({ ...current, loading: false, error: messageOf(error, '작업을 불러오지 못했습니다.') }));
      }
    };
    void load();
    return () => { active = false; window.clearTimeout(timer); };
  }, [drawerOpen, view, selectedId, port, fixtureMode, fixtureJobs, allowed, detailVersion, notifyCompleted]);

  const openCreate = useCallback((nextOperation: string) => {
    const access = capabilities.current;
    if (applying.current || submittingRef.current || !access.canCreate || !access.canRun || !access.allowed.includes(nextOperation)) return;
    setView(undefined);
    setSelectedId(undefined);
    setOperation(nextOperation);
  }, []);
  const openHistory = useCallback(() => {
    if (applying.current || submittingRef.current || !capabilities.current.canRead) return;
    setOperation(undefined);
    setSelectedId(undefined);
    setSubmitError('');
    setView('history');
    setHistoryVersion((value) => value + 1);
  }, []);
  const openJob = useCallback((id: string, operationType?: string, receipt?: BulkOperationReceipt) => {
    if (applying.current || submittingRef.current || !capabilities.current.canRead) return;
    if (operationType && !capabilities.current.allowed.includes(operationType)) return;
    setOperation(undefined);
    if (receipt) {
      receipts.current.set(id, receipt);
      if (receipts.current.size > 20) receipts.current.delete(receipts.current.keys().next().value!);
    }
    setSelectedOperation(operationType);
    setSelectedId(id);
    setDetail({ loading: true });
    setSubmitError('');
    setView('detail');
    setDetailVersion((value) => value + 1);
  }, []);
  const actions = useMemo(() => ({ openCreate, openHistory, openJob }), [openCreate, openHistory, openJob]);
  const closeDrawer = () => {
    if (submittingRef.current) return;
    setView(undefined);
    setSelectedId(undefined);
    setSubmitError('');
  };
  const closeDialog = () => {
    setOperation(undefined);
    if (selectedId) setView('detail');
  };
  const showSubmittedJob = (job: BulkOperationDetail) => {
    if (!mounted.current) return;
    setHistoryVersion((value) => value + 1);
    if (capabilities.current.canRead && capabilities.current.allowed.includes(job.operationType)) {
      setSelectedOperation(job.operationType);
      setSelectedId(job.id);
      detailContext.current = context.current.epoch;
      setDetail({ data: job, loading: false });
      setView('detail');
      setDetailVersion((value) => value + 1);
    }
  };

  const apply = async (operationType: string, rows: BulkValidatedRow[], idempotencyKey: string) => {
    const access = capabilities.current;
    if (!mounted.current || applying.current || !access.canCreate || !access.canRun || !access.allowed.includes(operationType)) throw new Error('현재 사용할 수 없는 기능입니다. 권한을 확인해 주세요.');
    applying.current = true;
    const epoch = context.current.epoch;
    try {
      const values = rows.map((row) => {
        const input = { ...row.values };
        // 대상 검색에만 사용하는 구성원 값은 휴가 정정 명령에 전송하지 않습니다.
        if (operationType === 'TIME_OFF_CORRECTION') delete input.workerId;
        return input;
      });
      const created: BulkOperationDetail = fixtureMode ? {
        id: `fixture-${idempotencyKey}`, operationType, originalFilename: `붙여넣기-${new Date().toISOString().slice(0, 10)}.tsv`,
        totalRows: rows.length, validRows: rows.length, succeededRows: 0, failedRows: 0, status: 'VALIDATED', createdAt: new Date().toISOString(),
        rows: rows.map((_, index) => ({ rowNumber: index + 1, status: 'VALID' })),
      } : await port.createBulkOperation(operationType, `붙여넣기-${new Date().toISOString().slice(0, 10)}.tsv`, values, idempotencyKey);
      if (!mounted.current || epoch !== context.current.epoch) throw new Error('작업 환경이 변경되었습니다. 처리 내역을 다시 확인해 주세요.');
      if (created.totalRows === 0 || created.validRows !== created.totalRows || created.failedRows > 0) {
        setHistoryVersion((value) => value + 1);
        assertBulkOperationValid(created, rows);
        throw new Error('작업의 검증 상태를 확인하지 못했습니다. 같은 내용으로 다시 시도하거나 처리 내역에서 결과를 확인해 주세요.');
      }
      if (!capabilities.current.canCreate || !capabilities.current.canRun || !capabilities.current.allowed.includes(operationType)) throw new Error('권한이 변경되어 작업을 적용할 수 없습니다.');
      // 재시도 시 같은 생성 키와 제출 키를 사용해 중복 작업을 방지합니다.
      const submitted = fixtureMode ? { ...created, status: 'QUEUED' } : await port.submitBulkOperation(created.id, `${idempotencyKey}:submit`);
      if (!mounted.current || epoch !== context.current.epoch) throw new Error('작업 환경이 변경되었습니다. 처리 내역을 다시 확인해 주세요.');
      const job = { ...created, ...submitted };
      if (fixtureMode) { saveFixtureBulkOperation(job); setFixtureJobs(listFixtureBulkOperations()); }
      setSubmitError('');
      showSubmittedJob(job);
      notifyCompleted([job], epoch);
      setNotice(`${created.totalRows.toLocaleString()}개 행 적용을 시작했습니다.`);
    } finally { applying.current = false; }
  };

  const selected = canRead && detailContext.current === context.current.epoch && detail.data && detail.data.id === selectedId && allowed.includes(detail.data.operationType) ? detail.data : undefined;
  const canSubmit = Boolean(selected && canRun && selected.status === 'VALIDATED' && selected.totalRows > 0 && selected.failedRows === 0 && selected.validRows === selected.totalRows);
  const submit = async () => {
    if (!mounted.current || !selected || !canSubmit || submittingRef.current) return;
    submittingRef.current = true;
    const epoch = context.current.epoch;
    setSubmitting(true);
    setSubmitError('');
    try {
      const submitted = fixtureMode ? { ...selected, status: 'QUEUED' } : await port.submitBulkOperation(selected.id, `${selected.id}:submit`);
      if (!mounted.current || epoch !== context.current.epoch) return;
      const job = { ...selected, ...submitted };
      if (fixtureMode) { saveFixtureBulkOperation(job); setFixtureJobs(listFixtureBulkOperations()); }
      if (capabilities.current.canRead && capabilities.current.allowed.includes(job.operationType)) { detailContext.current = epoch; setDetail({ data: job, loading: false }); }
      setDetailVersion((value) => value + 1);
      setHistoryVersion((value) => value + 1);
      notifyCompleted([job], epoch);
      setNotice(`${selected.totalRows.toLocaleString()}개 행 적용을 시작했습니다.`);
    } catch (error) {
      if (mounted.current && epoch === context.current.epoch) setSubmitError(messageOf(error, '작업을 적용하지 못했습니다. 다시 시도해 주세요.'));
    } finally { submittingRef.current = false; if (mounted.current) setSubmitting(false); }
  };

  const historyData = historyContext.current === context.current.epoch ? history.data : undefined;
  const staffingDialog = drawerOpen && view === 'detail' && (selected?.operationType ?? selectedOperation) === 'STAFFING_CHANGE';
  const historyRows = (historyData ?? []).filter((job) => allowed.includes(job.operationType)
    && (!status || job.status === status)
    && `${bulkOperationLabel(job.operationType)} ${job.originalFilename ?? ''}`.toLocaleLowerCase().includes(query.toLocaleLowerCase()));

  return <>
    {children(actions)}
    <FeedbackSnackbar open={Boolean(notice)} message={notice} tone="success" onClose={() => setNotice('')} />
    <DetailDrawer
      open={drawerOpen && !staffingDialog}
      onClose={closeDrawer}
      title={view === 'history' ? title : selected ? bulkOperationLabel(selected.operationType) : '작업 상세'}
      meta={view === 'detail' ? selected?.originalFilename : undefined}
      footer={view === 'detail' && (canSubmit || submitError) ? <Stack spacing={1}>
        {submitError && <Alert severity="error">{submitError}</Alert>}
        {canSubmit && <Button variant="fill" disabled={submitting || detail.loading} onClick={() => void submit()}>{submitting ? '적용 중' : '검증된 행 적용'}</Button>}
      </Stack> : undefined}
    >
      <DetailDrawerBody spacing={2}>
        {view === 'history' ? <>
          <SearchField value={query} onValueChange={(value) => { setQuery(value); setVisibleCount(25); }} placeholder="작업 또는 입력 검색" label="처리 내역 검색" />
          <FormTextField select label="상태" value={status} slotProps={{ select: { displayEmpty: true } }} onChange={(event) => { setStatus(event.target.value); setVisibleCount(25); }} fullWidth>
            <MenuItem value="">전체</MenuItem>
            {['DRAFT', 'VALIDATED', 'QUEUED', 'PROCESSING', 'COMPLETED', 'COMPLETED_WITH_ERRORS', 'FAILED', 'CANCELLED'].map((value) => <MenuItem key={value} value={value}>{bulkStatusLabel(value)}</MenuItem>)}
          </FormTextField>
          {history.error && historyData && <Alert severity="error" action={<Button color="inherit" size="small" onClick={() => setHistoryVersion((value) => value + 1)}>다시 조회</Button>}>{history.error}</Alert>}
          <ResourceState loading={history.loading && !historyData} loadingLabel="처리 내역 불러오는 중" error={historyData ? undefined : history.error} empty={historyRows.length === 0} emptyTitle={query || status ? '조건에 맞는 처리 내역이 없습니다.' : '아직 처리 내역이 없습니다.'} retryLabel="다시 조회" onRetry={() => setHistoryVersion((value) => value + 1)} embedded density="compact">
            <Stack spacing={1}>
              <Typography variant="body2" color="text.secondary">총 {historyRows.length.toLocaleString()}건 · 최근 요청순</Typography>
              <Box role="list" aria-label="대량 작업 처리 내역">
                {historyRows.slice(0, visibleCount).map((job) => <Box role="listitem" key={job.id}>
                  <ListRow label={bulkOperationLabel(job.operationType)} ariaLabel={`${bulkOperationLabel(job.operationType)} · ${bulkStatusLabel(job.status)} · ${job.originalFilename ?? ''}`} trailing={<BulkStatus value={job.status} />} description={<>{bulkDateLabel(job.createdAt)} · 전체 {job.totalRows.toLocaleString()}개<br />성공 {job.succeededRows.toLocaleString()}개 · 실패 {job.failedRows.toLocaleString()}개{job.originalFilename ? <><br />{job.originalFilename}</> : null}</>} onClick={() => openJob(job.id, job.operationType)} />
                  <Divider />
                </Box>)}
              </Box>
              {historyRows.length > visibleCount && <Button variant="outlined" onClick={() => setVisibleCount((value) => value + 25)}>더 보기 ({Math.min(visibleCount, historyRows.length).toLocaleString()} / {historyRows.length.toLocaleString()})</Button>}
            </Stack>
          </ResourceState>
        </> : <>
          <Stack direction="row" spacing={1} useFlexGap sx={{ justifyContent: 'space-between', flexWrap: 'wrap' }}><Button size="small" startIcon={<ArrowBackOutlined />} disabled={submitting} onClick={openHistory}>처리 내역으로</Button>{selected && <Button size="small" disabled={submitting || detail.loading} onClick={() => setDetailVersion((value) => value + 1)}>다시 조회</Button>}</Stack>
          {detail.error && selected && <Alert severity="error">{detail.error}</Alert>}
          <ResourceState loading={detail.loading && !selected} loadingLabel="작업 상세 불러오는 중" error={selected ? undefined : detail.error} empty={!selected} emptyTitle="작업을 찾을 수 없습니다." retryLabel="다시 조회" onRetry={() => setDetailVersion((value) => value + 1)} embedded density="compact">
            {selected && <BulkOperationResults job={selected} busy={submitting} onCorrect={canCreate && canRun ? () => openCreate(selected.operationType) : undefined} />}
          </ResourceState>
        </>}
      </DetailDrawerBody>
    </DetailDrawer>
    {staffingDialog && <BulkOperationResultDialog job={selected} loading={detail.loading} error={detail.error}
      submitError={submitError || undefined} busy={submitting} receipt={selectedId ? receipts.current.get(selectedId) : undefined}
      onClose={closeDrawer} onRefresh={() => setDetailVersion((value) => value + 1)}
      onSubmit={canSubmit ? () => void submit() : undefined}
      onCorrect={canCreate && canRun && selected ? () => { setOperation(selected.operationType); setView(undefined); } : undefined}
      onOpenStaffingChange={onOpenStaffingChange ? (id) => { closeDrawer(); onOpenStaffingChange(id); } : undefined} />}
    {canCreate && canRun && dialogWorkflows.length > 0 && <BulkPasteDialog open={Boolean(operation)} workflows={dialogWorkflows} referencePort={references} initialOperationType={operation} onClose={closeDialog} onApply={apply} />}
  </>;
}
